import { Trash2, Loader2 } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useLanguage } from "@/contexts/LanguageContext";

interface LightboxDeleteButtonProps {
  onDelete: () => Promise<void>;
  isDeleting?: boolean;
}

export function LightboxDeleteButton({ onDelete, isDeleting = false }: LightboxDeleteButtonProps) {
  const [open, setOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useLanguage();

  const handleConfirmDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Close dialog immediately for better UX
    setOpen(false);
    setIsProcessing(true);
    
    // Call delete - the parent will close the lightbox
    await onDelete();
    
    setIsProcessing(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          disabled={isDeleting || isProcessing}
          className={cn(
            "flex items-center gap-2 md:gap-3 w-full p-2.5 md:p-3 rounded-xl md:rounded-2xl",
            "bg-red-500/10 hover:bg-red-500/20 text-foreground font-bold transition-all active:scale-95 disabled:opacity-50"
          )}
        >
          <div className="w-8 h-8 md:w-9 md:h-9 bg-red-500/20 rounded-lg md:rounded-xl flex items-center justify-center">
            {isDeleting || isProcessing ? (
              <Loader2 className="w-4 h-4 text-red-400 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4 text-red-400" />
            )}
          </div>
          <span className="text-xs md:text-sm">
            {t.lightboxDelete.deleteBtn}
          </span>
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-card border-border z-[110]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-foreground">
            {t.lightboxDelete.confirmTitle}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-muted-foreground">
            {t.lightboxDelete.confirmDesc}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-secondary text-foreground border-border hover:bg-muted">
            {t.lightboxDelete.cancel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmDelete}
            className="bg-red-500 text-white hover:bg-red-600"
          >
            {t.lightboxDelete.deleteBtn}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
